/**
 * Helpers for the ManageListingsPage CSV export — maps Sharetribe
 * listing entities to flat CSV rows with Swedish column headers.
 *
 * Centralised here so the ManageListingsPage component stays focused
 * on UI plumbing and the duck stays focused on data fetching.
 */

import {
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { boltCircleText } from '../../util/boltCircleCore';
import { lifetimeViews } from '../../util/listingViews';
import { VISIBILITY_CATALOG } from '../../util/catalogueCore';

// Swedish labels for the listingType enum. Wheel and rim each have a
// "business-prefixed" variant (legacy naming) that maps to the same
// human-readable category.
export const LISTING_TYPE_LABELS = {
  wheel: 'Hjul',
  'business-wheel': 'Hjul',
  tire: 'Däck',
  businesstire: 'Däck',
  rim: 'Fälg',
  businesswheel: 'Fälg',
  buying: 'Sökes',
  'admin-adverts': 'Adminannons',
};

// Sharetribe Console schema for publicData.tiretype — these are the
// camelCase enum values actually stored on listings. Maps to the
// Swedish display labels users entered in Console.
const TIRETYPE_LABELS = {
  summer: 'Sommar',
  winterFriction: 'Vinter friktion',
  winterDubbed: 'Vinter dubbat',
  rdack: 'R-Däck',
  cdack: 'C-Däck',
};

export const EXPORT_HEADERS = [
  'Annons-ID',
  'Titel',
  'Typ',
  'Status',
  'Pris',
  'Valuta',
  'Skapad',
  'Uppdaterad',
  'Visningar',
  'Diameter',
  'Antal bultar',
  'Bultcirkel',
  'Däckbredd',
  'Profil',
  'Däcktyp',
];

const formatDate = date => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  // ISO YYYY-MM-DD — locale-agnostic, sortable, matches the filename
  // date format and avoids Excel auto-converting MM/DD/YYYY ambiguity.
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Price arrives as a Sharetribe Money instance (amount in subunits +
// currency) once denormalised. Convert subunits → major units.
const formatPrice = price => {
  if (!price || price.amount == null) return '';
  return (price.amount / 100).toFixed(2);
};

/**
 * The listing's status in one Swedish word.
 *
 * Exported because the redesigned listing rows (P25009-389 BR-3) show the same
 * status in list view, grid view and the CSV export. Three copies of this mapping
 * would be three chances to disagree about what "Dold" means.
 */
export const resolveStatus = (state, publicData) => {
  if (state === LISTING_STATE_DRAFT) return 'Utkast';
  /* P25009-410 BR-4 — a catalogue row is never called an annons: it is on the
   * seller's profile and kept out of marketplace search. Normally published;
   * one from before catalogue rows were published still awaits approval. */
  const isCatalogue = publicData?.visibility === VISIBILITY_CATALOG;
  if (state === LISTING_STATE_PENDING_APPROVAL) {
    return isCatalogue ? 'Katalog' : 'Väntar på godkännande';
  }
  if (state === LISTING_STATE_CLOSED) return 'Stängd';
  if (state === LISTING_STATE_PUBLISHED) {
    if (publicData?.isDeleted === true) return 'Borttagen';
    // P25009-485 BR-2 — the seller's own marker for a hidden trade-in.
    if (publicData?.isHide === true && publicData?.isTradein === true) return 'Inbyte · dold';
    if (publicData?.isHide === true) return 'Dold';
    if (isCatalogue) return 'Katalog';
    return 'Publicerad';
  }
  return state || '';
};

/**
 * Convert one listing entity to a flat array matching EXPORT_HEADERS.
 * Missing values become empty cells (rendered as `,,` in the CSV).
 *
 * @param {object} listing  Denormalised Sharetribe listing entity.
 * @param {object} [recordedViewsByListing] seller-stats' map, added
 *   to the frozen lifetime counter (src/util/listingViews.js).
 */
export const listingToCsvRow = (listing, recordedViewsByListing = {}) => {
  const id = listing?.id?.uuid || '';
  const attrs = listing?.attributes || {};
  const publicData = attrs.publicData || {};
  const metadata = attrs.metadata || {};
  const price = attrs.price;

  const tireIdLabel = metadata.tireId || '';
  const listingTypeLabel = LISTING_TYPE_LABELS[publicData.listingType] || publicData.listingType || '';
  const tireTypeLabel = TIRETYPE_LABELS[publicData.tiretype] || publicData.tiretype || '';

  // publicData.updateDate is written by EditListingDetailsPanel as
  // new Date().getTime() — a millisecond timestamp. Falls back to
  // createdAt when the listing has never been edited after creation.
  const updatedAtRaw = publicData.updateDate || attrs.createdAt;

  return [
    tireIdLabel || id,
    attrs.title || '',
    listingTypeLabel,
    resolveStatus(attrs.state, publicData),
    formatPrice(price),
    price?.currency || '',
    formatDate(attrs.createdAt),
    formatDate(updatedAtRaw),
    lifetimeViews(publicData, recordedViewsByListing?.[id]),
    publicData.diameter ?? '',
    publicData.numberOfBolts ?? '',
    // "114.3", not Console's key "114_3" / "1397" (P25009-488).
    boltCircleText(publicData.boltCircle) || (publicData.boltCircle ?? ''),
    publicData.tireWidthFront ?? '',
    publicData.profileFront ?? '',
    tireTypeLabel,
  ];
};

/**
 * Filename per EXP-AC-06: wheelplace-annonser-[YYYY-MM-DD].csv
 * using today's local date.
 */
export const buildExportFilename = (today = new Date()) =>
  `wheelplace-annonser-${formatDate(today)}.csv`;
